import React, { useRef, useState, useEffect } from 'react';
import './SignaturePad.scss';

interface SignaturePadProps {
    onSignatureCreate: (signatureDataUrl: string) => void;
    onClose: () => void;
    userName?: string;
}

type SignatureMode = 'draw' | 'type' | 'upload';

const SignaturePad: React.FC<SignaturePadProps> = ({ onSignatureCreate, onClose, userName = '' }) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [mode, setMode] = useState<SignatureMode>('draw');
    const [isDrawing, setIsDrawing] = useState(false);
    const [typedSignature, setTypedSignature] = useState(userName);
    const [selectedFont, setSelectedFont] = useState('Dancing Script');
    const [hasDrawing, setHasDrawing] = useState(false);
    const [uploadedImage, setUploadedImage] = useState<string | null>(null);

    const fonts = [
        { name: 'Dancing Script', label: 'Elegante' },
        { name: 'Great Vibes', label: 'Cursiva' },
        { name: 'Pacifico', label: 'Casual' },
        { name: 'Sacramento', label: 'Formal' },
        { name: 'Satisfy', label: 'Moderna' },
    ];

    useEffect(() => {
        if (mode === 'draw' && canvasRef.current) {
            const canvas = canvasRef.current;
            const ctx = canvas.getContext('2d');
            if (ctx) {
                ctx.fillStyle = 'white';
                ctx.fillRect(0, 0, canvas.width, canvas.height);
            }
        }
    }, [mode]);

    // Cargar fuentes de Google Fonts
    useEffect(() => {
        const link = document.createElement('link');
        link.href = 'https://fonts.googleapis.com/css2?family=Dancing+Script&family=Great+Vibes&family=Pacifico&family=Sacramento&family=Satisfy&display=swap';
        link.rel = 'stylesheet';
        document.head.appendChild(link);
        return () => {
            document.head.removeChild(link);
        };
    }, []);

    const startDrawing = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
        if (!canvasRef.current) return;
        
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        setIsDrawing(true);
        setHasDrawing(true);

        const rect = canvas.getBoundingClientRect();
        const x = ('touches' in e) 
            ? e.touches[0].clientX - rect.left 
            : e.clientX - rect.left;
        const y = ('touches' in e) 
            ? e.touches[0].clientY - rect.top 
            : e.clientY - rect.top;

        ctx.beginPath();
        ctx.moveTo(x, y);
    };

    const draw = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
        if (!isDrawing || !canvasRef.current) return;

        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const rect = canvas.getBoundingClientRect();
        const x = ('touches' in e) 
            ? e.touches[0].clientX - rect.left 
            : e.clientX - rect.left;
        const y = ('touches' in e) 
            ? e.touches[0].clientY - rect.top 
            : e.clientY - rect.top;

        ctx.lineWidth = 2;
        ctx.lineCap = 'round';
        ctx.strokeStyle = '#001849';
        ctx.lineTo(x, y);
        ctx.stroke();
    };

    const stopDrawing = () => {
        setIsDrawing(false);
    };

    const clearCanvas = () => {
        if (!canvasRef.current) return;
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        if (ctx) {
            ctx.fillStyle = 'white';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            setHasDrawing(false);
        }
    };

    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (event) => {
            setUploadedImage(event.target?.result as string);
        };
        reader.readAsDataURL(file);
    };

    const generateTypedSignature = (): string => {
        const canvas = document.createElement('canvas');
        canvas.width = 400;
        canvas.height = 150;
        const ctx = canvas.getContext('2d');
        
        if (!ctx) return '';

        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        ctx.font = `48px "${selectedFont}", cursive`;
        ctx.fillStyle = '#001849';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(typedSignature, canvas.width / 2, canvas.height / 2);

        return canvas.toDataURL('image/png');
    };

    const handleSave = () => {
        let signatureDataUrl = '';

        if (mode === 'draw' && canvasRef.current) {
            signatureDataUrl = canvasRef.current.toDataURL('image/png');
        } else if (mode === 'type') {
            signatureDataUrl = generateTypedSignature();
        } else if (mode === 'upload' && uploadedImage) {
            signatureDataUrl = uploadedImage;
        }

        if (signatureDataUrl) {
            onSignatureCreate(signatureDataUrl);
        }
    };

    const canSave = () => {
        if (mode === 'draw') return hasDrawing;
        if (mode === 'type') return typedSignature.trim().length > 0;
        if (mode === 'upload') return !!uploadedImage;
        return false;
    };

    return (
        <div className="signature-pad-container">
            {/* Tabs de modo */}
            <div className="signature-mode-tabs">
                <button 
                    className={`mode-tab ${mode === 'draw' ? 'active' : ''}`}
                    onClick={() => setMode('draw')}
                >
                    <i className="fas fa-pen me-2"></i>
                    Dibujar
                </button>
                {/* <button 
                    className={`mode-tab ${mode === 'type' ? 'active' : ''}`}
                    onClick={() => setMode('type')}
                >
                    <i className="fas fa-keyboard me-2"></i>
                    Escribir
                </button> */}
                <button 
                    className={`mode-tab ${mode === 'upload' ? 'active' : ''}`}
                    onClick={() => setMode('upload')}
                >
                    <i className="fas fa-upload me-2"></i>
                    Subir
                </button>
            </div>

            {/* Contenido según modo */}
            <div className="signature-content">
                {mode === 'draw' && (
                    <div className="draw-mode">
                        <p className="mode-instruction">
                            <i className="fas fa-info-circle me-2 text-primary"></i>
                            Dibuja tu firma usando el mouse o tu dedo
                        </p>
                        <canvas
                            ref={canvasRef}
                            width={400}
                            height={150}
                            className="signature-canvas"
                            onMouseDown={startDrawing}
                            onMouseMove={draw}
                            onMouseUp={stopDrawing}
                            onMouseLeave={stopDrawing}
                            onTouchStart={startDrawing}
                            onTouchMove={draw}
                            onTouchEnd={stopDrawing}
                        />
                        <button 
                            className="btn btn-outline-secondary btn-sm mt-2"
                            onClick={clearCanvas}
                        >
                            <i className="fas fa-eraser me-2"></i>
                            Borrar
                        </button>
                    </div>
                )}

                {mode === 'type' && (
                    <div className="type-mode">
                        <p className="mode-instruction">
                            <i className="fas fa-info-circle me-2 text-primary"></i>
                            Escribe tu nombre y selecciona un estilo de letra
                        </p>
                        <input
                            type="text"
                            className="form-control mb-3"
                            placeholder="Escribe tu nombre completo"
                            value={typedSignature}
                            onChange={(e) => setTypedSignature(e.target.value)}
                        />
                        <div className="font-selector">
                            {fonts.map((font) => (
                                <button
                                    key={font.name}
                                    className={`font-option ${selectedFont === font.name ? 'selected' : ''}`}
                                    onClick={() => setSelectedFont(font.name)}
                                    style={{ fontFamily: `"${font.name}", cursive` }}
                                >
                                    {typedSignature || 'Tu Nombre'}
                                </button>
                            ))}
                        </div>
                        <div className="signature-preview-box mt-3">
                            <span 
                                className="typed-signature-preview"
                                style={{ fontFamily: `"${selectedFont}", cursive` }}
                            >
                                {typedSignature || 'Tu Nombre'}
                            </span>
                        </div>
                    </div>
                )}

                {mode === 'upload' && (
                    <div className="upload-mode">
                        <p className="mode-instruction">
                            <i className="fas fa-info-circle me-2 text-primary"></i>
                            Sube una imagen de tu firma (PNG, JPG)
                        </p>
                        <div className="upload-area">
                            <input
                                type="file"
                                id="signature-upload"
                                accept="image/png,image/jpeg,image/jpg"
                                onChange={handleFileUpload}
                                hidden
                            />
                            <label htmlFor="signature-upload" className="upload-label">
                                {uploadedImage ? (
                                    <img 
                                        src={uploadedImage} 
                                        alt="Firma subida" 
                                        className="uploaded-signature-preview"
                                    />
                                ) : (
                                    <>
                                        <i className="fas fa-cloud-upload-alt fa-3x text-muted mb-2"></i>
                                        <span>Haz clic para seleccionar una imagen</span>
                                    </>
                                )}
                            </label>
                        </div>
                        {uploadedImage && (
                            <button 
                                className="btn btn-outline-secondary btn-sm mt-2"
                                onClick={() => setUploadedImage(null)}
                            >
                                <i className="fas fa-times me-2"></i>
                                Quitar imagen
                            </button>
                        )}
                    </div>
                )}
            </div>

            {/* Botones de acción */}
            <div className="signature-actions">
                <button className="btn btn-outline-secondary" onClick={onClose}>
                    Cancelar
                </button>
                <button 
                    className="btn btn-primary"
                    onClick={handleSave}
                    disabled={!canSave()}
                >
                    <i className="fas fa-check me-2"></i>
                    Usar esta firma
                </button>
            </div>
        </div>
    );
};

export default SignaturePad;
